import { Component, Element, Event, EventEmitter, Host, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { type FetchResponse } from '@sydle/ui-api';
import { type BlogPostPreview } from '../../../../types/blog';
import { renderComponent } from '../../../../utils/render-utils';
import { ComponentContentType } from '../../sy-blog-share-bar/share-bar.types';

@Component({
  tag: 'sy-blog-card-list',
  styleUrl: './scss/index.scss',
  shadow: true,
})
export class SyBlogCardList {
  @Element() el: HTMLSyBlogCardListElement;

  /**
   * Defines if infinite scroll is enabled
   */
  @Prop() infiniteScroll: boolean;

  /**
   * Size of fetched pages
   */
  @Prop() pageSize: number;

  /**
   * Label of the button that requests more content
   */
  @Prop() contentRequestLabel: string;

  /**
   * Label of the button that goes back to top
   */
  @Prop() backToTopLabel: string;

  /**
   * Message to display when there are no posts to load.
   */
  @Prop() emptyListMessage: string;

  /**
   * Banner JSON component Prop
   */
  @Prop() bannerItems: ComponentContentType;

  /**
   * Current page
   */
  @State() pageIndex = -1;

  /**
   * Total number of previews available to fetch
   */
  @Prop({ mutable: true }) total: number;

  /**
   * Fetched post previews
   */
  @Prop({ mutable: true }) posts: BlogPostPreview[] = [];

  /**
   * Indicates if cards are being fetched
   */
  @State() loading = false;

  /**
   * Emitted when more content is requested
   */
  @Event() syCardListDidAskForMoreContent: EventEmitter<{ pageIndex: number; pageSize: number }>;

  private bannerPositions: number[] = [];
  private bannerNumber = 0;

  private requestMoreContent(): void {
    this.pageIndex++;

    if (this.pageIndex >= Math.ceil(this.total / this.pageSize)) {
      return;
    }

    this.loading = true;
    this.syCardListDidAskForMoreContent.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
  }

  /**
   * Resets card list to initial state
   */
  @Method()
  async resetList(): Promise<void> {
    this.pageIndex = -1;
    this.posts = [];
    this.requestMoreContent();
  }

  /**
   * Function to get cards
   */
  @Prop() getCards: (
    pageIndex: number,
    pageSize: number,
  ) => Promise<FetchResponse<{ posts: BlogPostPreview; total: number }>>;

  @Listen('syCardListDidAskForMoreContent')
  selfGetCards(): void {
    if (this.getCards) {
      if ((window as any).preloadPostsList) {
        this.total = (window as any).preloadPostsList?.total || 0;
        this.posts = this.posts.concat((window as any).preloadPostsList?.posts as BlogPostPreview);
        const node = document.getElementById('preloadPostsList');
        if (node?.parentNode) {
          node.parentNode.removeChild(node);
        }
        delete (window as any).preloadPostsList;
      } else {
        this.getCards(this.pageIndex, this.pageSize).then((response) => {
          this.total = response.data?.total || 0;
          this.posts = this.posts.concat(response.data?.posts as BlogPostPreview);
        });
      }
    }
  }

  @Watch('posts')
  postsLoaded(): void {
    this.loading = false;
  }

  private intersectionObserver: IntersectionObserver = new IntersectionObserver(this.intersectionHandler.bind(this), {
    threshold: 0.5,
  });
  private intersectionHandler(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
    if (entries.some((entry) => entry.isIntersecting) && this.posts.length < this.total) {
      this.requestMoreContent();
      observer.disconnect();
    }
  }

  private selectBanner() {
    const selectedBannerComponent = JSON.parse(JSON.stringify(this.bannerItems));

    selectedBannerComponent.node.bannerItems = [
      JSON.parse(JSON.stringify(this.bannerItems.node.bannerItems[this.bannerNumber])),
    ];
    this.bannerNumber++;
    if (this.bannerNumber >= this.bannerItems.node.bannerItems.length) {
      this.bannerNumber = 0;
    }
    return selectedBannerComponent;
  }

  componentWillLoad() {
    this.requestMoreContent();
  }

  componentWillRender() {
    this.bannerNumber = 0;
    for (let i = this.pageSize - 1; i < this.posts.length; i += this.pageSize) {
      this.bannerPositions.push(i);
    }
    if (this.posts.length > 0 && !this.bannerPositions.includes(this.posts.length - 1)) {
      this.bannerPositions.push(this.posts.length - 1);
    }
  }

  componentDidRender() {
    if (this.infiniteScroll && this.posts.length < this.total && this.el.shadowRoot && !this.loading) {
      this.intersectionObserver.observe(this.el.shadowRoot.querySelector('.see-more') as HTMLElement);
    }
  }

  render() {
    return (
      <Host>
        <div class="grid">
          {this.posts.map((post, index) => {
            if (this.bannerItems?.node?.bannerItems?.length > 0 && this.bannerPositions.includes(index)) {
              const selectedBannerComponent = this.selectBanner();
              return (
                <div key={post._id} class="card-and-banner-wrapper">
                  <sy-blog-card class="card" post={post} />
                  <div class="banner">{selectedBannerComponent ? renderComponent(selectedBannerComponent) : null}</div>
                </div>
              );
            } else {
              return <sy-blog-card class="card" post={post} key={post._id} />;
            }
          })}
          {this.loading &&
            (this.posts.length < this.total || !this.total) &&
            new Array(this.pageSize).fill(0).map((_, i) => {
              return <sy-blog-card class="card" skeleton key={i} />;
            })}
        </div>
        <div class="see-more" style={{ minHeight: '1px' }}>
          <sy-button
            id="request-content"
            label={this.posts.length == this.total ? this.backToTopLabel : this.contentRequestLabel}
            disabled={this.loading}
            variant="filled"
            color="secondary"
            onClick={() => this.requestMoreContent()}
            style={(this.infiniteScroll || this.posts.length == this.total) && { display: 'none' }}
          />
          <sy-button
            id="back-to-top"
            variant="filled"
            color="secondary"
            onClick={() => window.scrollTo({ behavior: 'smooth', top: 0, left: 0 })}
            style={(this.posts.length == 0 || this.posts.length != this.total) && { display: 'none' }}
          >
            <sy-icon name="arrow_upward" />
            {this.backToTopLabel}
          </sy-button>
          {!this.posts.length && !this.total && !this.loading && this.emptyListMessage ? (
            <sy-text type="body">{this.emptyListMessage}</sy-text>
          ) : null}
        </div>
      </Host>
    );
  }
}
